import { create } from 'zustand';
import type { Event, Outfit, OutfitUpdate, Template } from '@/types';
import { useAuthStore } from './authStore';

const API_URL = import.meta.env.VITE_API_URL || 'https://cannes.direzione-marketingtools.workers.dev/api';

// Debounce map for auto-save (prevents race conditions on rapid edits)
const pendingUpdates = new Map<number, ReturnType<typeof setTimeout>>();

interface Pagination {
  page: number;
  totalPages: number;
  total: number;
}

interface OutfitState {
  events: Event[];
  eventsPagination: Pagination | null;
  currentEvent: Event | null;
  outfits: Outfit[];
  currentOutfit: Outfit | null;
  currentDay: number;
  selectedTemplate: Template | null;
  isLoading: boolean;
  error: string | null;
  autoSaveStatus: 'idle' | 'saving' | 'saved' | 'error';

  // Actions
  fetchEvents: () => Promise<void>;
  fetchMoreEvents: () => Promise<void>;
  createEvent: (name: string, totalDays?: number) => Promise<Event | null>;
  deleteEvent: (eventId: number) => Promise<boolean>;
  setCurrentEvent: (event: Event | null) => void;
  fetchOutfits: (eventId: number) => Promise<void>;
  updateEventDays: (eventId: number, totalDays: number) => Promise<boolean>;
  updateOutfit: (outfitId: number, updates: OutfitUpdate) => Promise<boolean>;
  setCurrentDay: (day: number) => void;
  setSelectedTemplate: (template: Template | null) => void;
  getCurrentOutfit: () => Outfit | null;
  uploadImage: (file: File, category: string) => Promise<string | null>;
  deleteImage: (filename: string) => Promise<boolean>;
  clearError: () => void;
}

export const useOutfitStore = create<OutfitState>()((set, get) => ({
  events: [],
  eventsPagination: null,
  currentEvent: null,
  outfits: [],
  currentOutfit: null,
  currentDay: 1,
  selectedTemplate: null,
  isLoading: false,
  error: null,
  autoSaveStatus: 'idle',

  fetchEvents: async () => {
    const authHeaders = useAuthStore.getState().getAuthHeaders();
    if (!authHeaders['Authorization']) return;

    set({ isLoading: true, error: null });
    try {
      const response = await fetch(`${API_URL}/events`, {
        headers: authHeaders,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch events');
      }

      const data = await response.json();
      set({ events: data.events || [], eventsPagination: data.pagination || null, isLoading: false });
    } catch (err) {
      console.error('Fetch events error:', err);
      set({ error: err instanceof Error ? err.message : 'Failed to load events', isLoading: false });
    }
  },

  fetchMoreEvents: async () => {
    const { eventsPagination, events } = get();
    if (!eventsPagination || eventsPagination.page >= eventsPagination.totalPages) return;

    const authHeaders = useAuthStore.getState().getAuthHeaders();
    if (!authHeaders['Authorization']) return;

    const nextPage = eventsPagination.page + 1;
    try {
      const response = await fetch(`${API_URL}/events?page=${nextPage}`, {
        headers: authHeaders,
      });
      if (!response.ok) return;
      const data = await response.json();
      set({
        events: [...events, ...(data.events || [])],
        eventsPagination: data.pagination || null,
      });
    } catch (err) {
      console.error('Fetch more events error:', err);
    }
  },

  createEvent: async (name: string, totalDays: number = 12) => {
    const authHeaders = useAuthStore.getState().getAuthHeaders();
    if (!authHeaders['Authorization']) return null;

    set({ isLoading: true, error: null });
    try {
      const response = await fetch(`${API_URL}/events`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, total_days: totalDays }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create event');
      }

      const data = await response.json();

      // Refresh events list
      await get().fetchEvents();

      set({ isLoading: false });
      return data.event;
    } catch (err) {
      console.error('Create event error:', err);
      set({ error: err instanceof Error ? err.message : 'Failed to create event', isLoading: false });
      return null;
    }
  },

  deleteEvent: async (eventId: number) => {
    const authHeaders = useAuthStore.getState().getAuthHeaders();
    if (!authHeaders['Authorization']) return false;

    try {
      const response = await fetch(`${API_URL}/events/${eventId}`, {
        method: 'DELETE',
        headers: authHeaders,
      });

      if (!response.ok) return false;

      set(state => ({ events: state.events.filter(e => e.id !== eventId) }));
      return true;
    } catch (error) {
      console.error('Delete event error:', error);
      return false;
    }
  },

  setCurrentEvent: (event: Event | null) => {
    set({ currentEvent: event, currentDay: 1 });
    if (event) {
      get().fetchOutfits(event.id);
    }
  },

  fetchOutfits: async (eventId: number) => {
    const authHeaders = useAuthStore.getState().getAuthHeaders();
    if (!authHeaders['Authorization']) return;

    set({ isLoading: true, error: null });
    try {
      const response = await fetch(`${API_URL}/events/${eventId}`, {
        headers: authHeaders,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch outfits');
      }

      const data = await response.json();
      const outfits = (data.outfits || []).map((o: Outfit) => ({
        ...o,
        hair_images: Array.isArray(o.hair_images) ? o.hair_images : JSON.parse(o.hair_images || '[]'),
        jewelry_images: Array.isArray(o.jewelry_images) ? o.jewelry_images : JSON.parse(o.jewelry_images || '[]'),
        purse_images: Array.isArray(o.purse_images) ? o.purse_images : JSON.parse(o.purse_images || '[]'),
        shoes_images: Array.isArray(o.shoes_images) ? o.shoes_images : JSON.parse(o.shoes_images || '[]'),
        outfit_images: Array.isArray(o.outfit_images) ? o.outfit_images : JSON.parse(o.outfit_images || '[]'),
      }));

      // Also set currentEvent from API response (avoids hardcoded mock)
      if (data.event) {
        set({ outfits, isLoading: false, currentEvent: data.event });
      } else {
        set({ outfits, isLoading: false });
      }

      // Set current outfit based on current day
      const { currentDay } = get();
      const currentOutfit = outfits.find((o: Outfit) => o.day_number === currentDay);
      set({ currentOutfit });
    } catch (err) {
      console.error('Fetch outfits error:', err);
      set({ error: err instanceof Error ? err.message : 'Failed to load outfits', isLoading: false });
    }
  },

  updateEventDays: async (eventId: number, totalDays: number) => {
    const authHeaders = useAuthStore.getState().getAuthHeaders();
    if (!authHeaders['Authorization']) return false;

    set({ isLoading: true, error: null });
    try {
      const response = await fetch(`${API_URL}/events/${eventId}/days`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ total_days: totalDays }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update event days');
      }

      const { currentEvent } = get();
      if (currentEvent?.id === eventId) {
        set({ currentEvent: { ...currentEvent, total_days: totalDays } });
        await get().fetchOutfits(eventId);
      }
      
      set({ isLoading: false });
      return true;
    } catch (err) {
      console.error('Update event days error:', err);
      set({ error: err instanceof Error ? err.message : 'Failed to update event days', isLoading: false });
      return false;
    }
  },

  updateOutfit: async (outfitId: number, updates: OutfitUpdate) => {
    const authHeaders = useAuthStore.getState().getAuthHeaders();
    if (!authHeaders['Authorization']) return false;

    // Update local state immediately for responsiveness
    const { outfits, currentOutfit } = get();
    const updatedOutfits = outfits.map(o =>
      o.id === outfitId ? { ...o, ...updates } : o
    );
    set({
      outfits: updatedOutfits,
      currentOutfit: currentOutfit?.id === outfitId ? { ...currentOutfit, ...updates } : currentOutfit,
      autoSaveStatus: 'saving',
    });

    // Debounce: cancel previous pending save for this outfit
    const existing = pendingUpdates.get(outfitId);
    if (existing) clearTimeout(existing);

    return new Promise<boolean>((resolve) => {
      pendingUpdates.set(outfitId, setTimeout(async () => {
        pendingUpdates.delete(outfitId);
        try {
          const response = await fetch(`${API_URL}/outfits/${outfitId}`, {
            method: 'PUT',
            headers: { ...authHeaders, 'Content-Type': 'application/json' },
            body: JSON.stringify(updates),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || 'Failed to update outfit');
          }

          set({ autoSaveStatus: 'saved' });
          setTimeout(() => set({ autoSaveStatus: 'idle' }), 2000);
          resolve(true);
        } catch (error) {
          console.error('Update outfit error:', error);
          set({ autoSaveStatus: 'error' });
          resolve(false);
        }
      }, 500));
    });
  },

  setCurrentDay: (day: number) => {
    const { outfits } = get();
    const currentOutfit = outfits.find(o => o.day_number === day) || null;
    set({ currentDay: day, currentOutfit });
  },

  setSelectedTemplate: (template) => {
    set({ selectedTemplate: template });
  },

  getCurrentOutfit: () => {
    const { outfits, currentDay } = get();
    return outfits.find(o => o.day_number === currentDay) || null;
  },

  uploadImage: async (file: File, category: string) => {
    const authHeaders = useAuthStore.getState().getAuthHeaders();
    if (!authHeaders['Authorization']) return null;

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category);

      const response = await fetch(`${API_URL}/upload/direct`, {
        method: 'POST',
        headers: authHeaders,
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Upload failed');
      }

      const data = await response.json();
      return data.url;
    } catch (err) {
      console.error('Upload error:', err);
      return null;
    }
  },

  deleteImage: async (filename: string) => {
    const authHeaders = useAuthStore.getState().getAuthHeaders();
    if (!authHeaders['Authorization']) return false;

    try {
      const response = await fetch(`${API_URL}/images/delete`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename }),
      });

      return response.ok;
    } catch (error) {
      console.error('Delete image error:', error);
      return false;
    }
  },

  clearError: () => {
    set({ error: null });
  },
}));
